import { useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { ListPanelContext } from '@/components/listPanelContext';

interface ListPanelProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
}

/** Inline list controls: the table stays visible and interactive. */
export const ListPanel = ({ isOpen, onClose, title, children, footer }: ListPanelProps) => {
    const { t } = useTranslation('global');
    const id = useId();
    const context = useContext(ListPanelContext);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const trigger = useRef<HTMLElement | null>(null);
    const section = useRef<HTMLElement | null>(null);
    const [mounted, setMounted] = useState(isOpen);
    const [closing, setClosing] = useState(false);
    useEffect(() => {
        if (isOpen) {
            setMounted(true);
            setClosing(false);
        } else if (mounted) {
            setClosing(true);
            const timer = window.setTimeout(() => {
                setMounted(false);
                setClosing(false);
            }, 160);

            return () => window.clearTimeout(timer);
        }

        if (!isOpen) {
            return;
        }
        trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        section.current?.querySelector<HTMLElement>('input:not([disabled]),button[role="combobox"]:not([disabled]),[role="switch"]:not([disabled])')?.focus();
        if(context?.target && context.target.getBoundingClientRect().bottom > window.innerHeight - 120){
            context.target.parentElement?.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
        return context?.activate(id, () => closeRef.current());
    }, [isOpen, id, context]);
    const close = (): void => { onClose(); trigger.current?.focus(); };
    if (!mounted) {
        return null;
    }
    const content = <section ref={section} role="region" aria-labelledby={`${id}-title`} className={`w-full rounded-lg border border-slate-200 bg-white p-3 dark:border-white/15 dark:bg-dark-box ${closing ? 'list-panel--closing' : 'list-panel--open'}`}
        onKeyDown={event => { if(event.key === 'Escape' && !event.defaultPrevented){ event.preventDefault(); event.stopPropagation(); close(); } }}>
        <div className="mb-3 flex items-center justify-between gap-2">
            <h2 id={`${id}-title`} className="text-sm font-semibold text-slate-800 dark:text-zinc-100">{title}</h2>
            <Button variant="ghost" size="xs" icon="x" onlyicon className="!text-slate-600 dark:!text-zinc-300" aria-label={t('workspace.list.close_panel')} onClick={close} />
        </div>
        <div className="max-h-[32dvh] overflow-y-auto overscroll-contain p-1">{children}</div>
        {footer && <div className="mt-3 flex flex-wrap items-center gap-2">{footer}</div>}
    </section>;
    return context?.target ? createPortal(content, context.target) : content;
};
